/**
 * Editor da landing de captação (/consultora) — aba "Landing" do painel Captação.
 *
 * Split: à esquerda a lista dos blocos com seus campos, à direita a landing
 * real num iframe. Cada tecla digitada vai pro iframe por postMessage, então o
 * preview reflete a edição antes de salvar; o botão Salvar é que persiste.
 *
 * Os formulários não são escritos à mão: saem de `BLOCKS` (src/landing/blueprint.ts).
 * Campo novo lá = campo novo aqui, sem tocar neste arquivo.
 */

import { useState, useEffect, useCallback, useRef } from 'react'
import {
  FiChevronDown, FiChevronRight, FiEye, FiEyeOff, FiArrowUp, FiArrowDown,
  FiSave, FiRotateCcw, FiExternalLink, FiPlus, FiTrash2, FiUpload, FiLoader,
  FiCrosshair, FiChevronLeft, FiCornerUpLeft,
  FiAlignLeft, FiAlignCenter, FiAlignRight, FiX, FiItalic, FiUnderline,
} from 'react-icons/fi'
import { useAuth } from '../contexts/AuthContext'
import { API_ENDPOINTS } from '../lib/api'
// Mora em arquivo próprio desde a migração para o editor visual: o Puck o usa
// como campo `custom` e os dois editores precisam do mesmo upload.
import CampoImagem from './CampoImagem'
import {
  BLOCK_BY_ID, emptyConfig, normalizeConfig, isListField, estiloCampo,
  chaveEstilo, nomeFonteSeguro,
  type BlockId, type BlockDef, type Field, type SimpleField,
  type LandingConfig, type ListItem, type AlignCampo, type EstiloCampo,
} from '../landing/blueprint'
import {
  PREVIEW_MESSAGE, PREVIEW_PRONTO, PREVIEW_SELECIONAR, PREVIEW_EDITAR,
  PREVIEW_REORDENAR, PREVIEW_FOCAR,
} from '../landing/preview'
import {
  COMBOS, COMBO_LABELS, PAD_LABELS, SHAPE_LABELS, ALIGN_CAMPO_LABELS, resolveCombo,
  PESO_CAMPO_LABELS, CORES_MARCA, TAM_MIN, TAM_MAX,
  ENTRELINHA_LABELS, ESPACAMENTO_LABELS,
  type ComboName, type PadName, type ShapeName, type ColorName,
  type PesoCampo, type EntrelinhaCampo, type EspacamentoCampo,
} from '../landing/theme'

const LANDING_PATH = '/consultora'

/** ids estáveis para itens de lista — só precisam ser únicos dentro da config. */
const newId = () => `i_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 7)}`

// ─── Controles reutilizáveis ───────────────────────────────────────────────

function Seg<T extends string>({ value, options, onChange }: {
  value: T
  options: { value: T; label: string }[]
  onChange: (v: T) => void
}) {
  return (
    <div className="inline-flex rounded-md border border-border overflow-hidden">
      {options.map(o => (
        <button
          key={o.value}
          type="button"
          onClick={() => onChange(o.value)}
          className={`h-7 px-2.5 text-[11px] transition-colors ${
            value === o.value ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
          }`}
        >
          {o.label}
        </button>
      ))}
    </div>
  )
}

/** Cabeçalho que separa conteúdo de aparência dentro do bloco aberto. */
function Grupo({ titulo }: { titulo: string }) {
  return (
    <div className="mt-1 flex items-center gap-2 pt-1">
      <span className="text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground/70">
        {titulo}
      </span>
      <span className="h-px flex-1 bg-border" />
    </div>
  )
}

function Rotulo({ children }: { children: React.ReactNode }) {
  return <span className="text-[11px] font-medium text-muted-foreground">{children}</span>
}

const inputCls =
  'w-full h-8 rounded-md border border-border bg-background px-2 text-xs text-foreground outline-none focus:border-primary/60'

/** Um campo simples (texto, textarea, link ou imagem). */
function CampoSimples({ field, value, onChange }: {
  field: SimpleField; value: string; onChange: (v: string) => void
}) {
  return (
    <label className="flex flex-col gap-1">
      <Rotulo>{field.label}</Rotulo>
      {field.type === 'image' ? (
        <CampoImagem value={value} onChange={onChange} />
      ) : field.type === 'textarea' ? (
        <textarea
          value={value} onChange={e => onChange(e.target.value)} rows={3}
          className={`${inputCls} h-auto py-1.5 resize-y`}
        />
      ) : (
        <input
          value={value} onChange={e => onChange(e.target.value)}
          placeholder={field.type === 'link' ? 'https://… ou #ancora' : ''}
          className={inputCls}
        />
      )}
      {field.hint && <span className="text-[10px] text-muted-foreground">{field.hint}</span>}
    </label>
  )
}

/** Lista de itens repetíveis (cards, perguntas, redes…). */
function CampoLista({ field, items, onChange, abrirIndice }: {
  field: Extract<Field, { type: 'list' }>
  items: ListItem[]
  onChange: (v: ListItem[]) => void
  /** Item que o preview pediu para abrir, quando o clique foi num card. */
  abrirIndice?: number
}) {
  const [aberto, setAberto] = useState<string | null>(null)

  // Clique na página num item da lista: abre exatamente aquele.
  useEffect(() => {
    if (abrirIndice === undefined) return
    const alvo = items[abrirIndice]
    if (alvo) setAberto(alvo._id)
  }, [abrirIndice, items])

  const alterar = (id: string, key: string, v: string) =>
    onChange(items.map(it => (it._id === id ? { ...it, [key]: v } : it)))

  const mover = (i: number, delta: number) => {
    const j = i + delta
    if (j < 0 || j >= items.length) return
    const copia = [...items]
    ;[copia[i], copia[j]] = [copia[j], copia[i]]
    onChange(copia)
  }

  const adicionar = () => {
    const novo = { _id: newId() } as ListItem
    for (const f of field.fields) novo[f.key] = ''
    onChange([...items, novo])
    setAberto(novo._id)
  }

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center justify-between">
        <Rotulo>{field.label}</Rotulo>
        <button
          type="button" onClick={adicionar}
          className="inline-flex items-center gap-1 text-[11px] text-primary hover:opacity-80 transition-opacity"
        >
          <FiPlus className="w-3 h-3" /> Adicionar {field.itemLabel}
        </button>
      </div>

      {items.length === 0 && (
        <p className="rounded-md border border-dashed border-border px-3 py-4 text-center text-[11px] text-muted-foreground">
          Nenhum item ainda.
        </p>
      )}

      <div className="flex flex-col gap-1">
        {items.map((it, i) => {
          const open = aberto === it._id
          const resumo = field.fields.map(f => it[f.key]).find(Boolean) || `${field.itemLabel} ${i + 1}`
          return (
            <div key={it._id} className="rounded-md border border-border">
              <div className="flex items-center gap-1 px-2 py-1.5">
                <button
                  type="button" onClick={() => setAberto(open ? null : it._id)}
                  className="flex flex-1 items-center gap-1.5 min-w-0 text-left text-[11px] text-foreground"
                >
                  {open ? <FiChevronDown className="w-3 h-3 shrink-0" /> : <FiChevronRight className="w-3 h-3 shrink-0" />}
                  <span className="truncate">{resumo}</span>
                </button>
                <button type="button" onClick={() => mover(i, -1)} disabled={i === 0}
                  className="p-1 text-muted-foreground hover:text-foreground disabled:opacity-25" title="Subir">
                  <FiArrowUp className="w-3 h-3" />
                </button>
                <button type="button" onClick={() => mover(i, 1)} disabled={i === items.length - 1}
                  className="p-1 text-muted-foreground hover:text-foreground disabled:opacity-25" title="Descer">
                  <FiArrowDown className="w-3 h-3" />
                </button>
                <button type="button" onClick={() => onChange(items.filter(x => x._id !== it._id))}
                  className="p-1 text-muted-foreground hover:text-red-400" title="Remover">
                  <FiTrash2 className="w-3 h-3" />
                </button>
              </div>
              {open && (
                <div className="flex flex-col gap-2.5 border-t border-border px-2.5 py-2.5">
                  {field.fields.map(f => (
                    <CampoSimples
                      key={f.key} field={f} value={it[f.key] || ''}
                      onChange={v => alterar(it._id, f.key, v)}
                    />
                  ))}
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

/** Controles de aparência e posicionamento do bloco. */
function ControlesEstilo({ def, config, setConfig, compacto = false }: {
  def: BlockDef
  config: LandingConfig
  setConfig: (fn: (c: LandingConfig) => LandingConfig) => void
  /** No balão só entram cor e posição — o resto continua na coluna lateral. */
  compacto?: boolean
}) {
  const style = config.blocks[def.id].style
  const patch = (p: Partial<typeof style>) =>
    setConfig(c => ({
      ...c,
      blocks: { ...c.blocks, [def.id]: { ...c.blocks[def.id], style: { ...c.blocks[def.id].style, ...p } } },
    }))

  const combo = resolveCombo(style.combo, style.textOn)

  return (
    <div className="flex flex-col gap-2.5 rounded-md border border-border bg-muted/20 p-2.5">
      {/* Cor: só os pares aprovados no guia de marca. */}
      <div className="flex flex-col gap-1.5">
        <Rotulo>Cor de fundo</Rotulo>
        <div className="flex items-center gap-1.5">
          {(Object.keys(COMBOS) as ComboName[]).map(nome => (
            <button
              key={nome} type="button"
              onClick={() => patch({ combo: nome, textOn: undefined })}
              title={COMBO_LABELS[nome]}
              className={`h-7 w-7 rounded-md border-2 transition-transform ${
                style.combo === nome ? 'border-primary scale-105' : 'border-border'
              }`}
              style={{ background: COMBOS[nome].bg }}
            />
          ))}
          <span className="ml-1 text-[10px] text-muted-foreground">{COMBO_LABELS[style.combo]}</span>
        </div>
      </div>

      {/* O marrom é o único fundo com duas cores de texto aprovadas. */}
      {COMBOS[style.combo].fgAlt && (
        <div className="flex flex-col gap-1.5">
          <Rotulo>Cor do texto</Rotulo>
          <Seg<ColorName>
            value={(combo.fg === COMBOS[style.combo].fg ? 'creme' : 'laranja') as ColorName}
            options={[{ value: 'creme' as ColorName, label: 'Creme' }, { value: 'laranja' as ColorName, label: 'Laranja' }]}
            onChange={v => patch({ textOn: v })}
          />
        </div>
      )}

      <div className="flex flex-wrap items-end gap-x-4 gap-y-2.5">
        <div className="flex flex-col gap-1.5">
          <Rotulo>Alinhamento</Rotulo>
          <Seg
            value={style.align}
            options={[{ value: 'left', label: 'Esquerda' }, { value: 'center', label: 'Centro' }]}
            onChange={v => patch({ align: v })}
          />
        </div>

        {def.caps?.mediaSide && (
          <div className="flex flex-col gap-1.5">
            <Rotulo>Lado da imagem</Rotulo>
            <Seg
              value={style.mediaSide}
              options={[{ value: 'left', label: 'Esquerda' }, { value: 'right', label: 'Direita' }]}
              onChange={v => patch({ mediaSide: v })}
            />
          </div>
        )}

        <div className="flex flex-col gap-1.5">
          <Rotulo>Espaçamento</Rotulo>
          <Seg<PadName>
            value={style.pad}
            options={(Object.keys(PAD_LABELS) as PadName[]).map(p => ({ value: p, label: PAD_LABELS[p] }))}
            onChange={v => patch({ pad: v })}
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <Rotulo>Borda</Rotulo>
          <Seg<ShapeName>
            value={style.shape}
            options={(Object.keys(SHAPE_LABELS) as ShapeName[]).map(s => ({
              value: s, label: s === 'none' ? 'Reta' : s === 'topo' ? 'Cima' : s === 'base' ? 'Baixo' : 'Ambas',
            }))}
            onChange={v => patch({ shape: v })}
          />
        </div>
      </div>

      {/* Padrão de fundo — fora do balão, que é só cor e posição. */}
      {!compacto && (
      <div className="flex flex-col gap-2.5 border-t border-border pt-2.5">
        <label className="flex cursor-pointer items-center gap-2">
          <input
            type="checkbox"
            checked={style.textura === true}
            onChange={e => patch({ textura: e.target.checked })}
            className="h-3.5 w-3.5 accent-primary"
          />
          <Rotulo>Padrão de fundo</Rotulo>
        </label>

        {style.textura && (
          <>
            <div className="flex flex-col gap-1">
              <Rotulo>Imagem do padrão</Rotulo>
              <CampoImagem
                value={style.texturaImg || ''}
                onChange={v => patch({ texturaImg: v || undefined })}
              />
              <span className="text-[10px] text-muted-foreground">
                Vazio usa a malha de pontos embutida, que assume a cor do texto do bloco.
                Envie um PNG com fundo transparente para repetir sem emenda.
              </span>
            </div>

            <label className="flex flex-col gap-1">
              <Rotulo>Tamanho do ladrilho ({style.texturaTam ?? (style.texturaImg ? 320 : 40)}px)</Rotulo>
              <input
                type="range" min={20} max={600} step={10}
                value={style.texturaTam ?? (style.texturaImg ? 320 : 40)}
                onChange={e => patch({ texturaTam: Number(e.target.value) })}
                className="w-full accent-primary"
              />
            </label>

            {/* Décimos de por cento: o padrão bonito fica na casa dos 3%, e um
                passo de 1% era grosso demais para ajustar isso. */}
            <label className="flex flex-col gap-1">
              <Rotulo>Intensidade ({((style.texturaOp ?? 0.032) * 100).toFixed(1)}%)</Rotulo>
              <input
                type="range" min={5} max={300} step={1}
                value={Math.round((style.texturaOp ?? 0.032) * 1000)}
                onChange={e => patch({ texturaOp: Number(e.target.value) / 1000 })}
                className="w-full accent-primary"
              />
            </label>
          </>
        )}
      </div>
      )}

      {def.caps?.height && (
        <label className="flex flex-col gap-1.5">
          <Rotulo>Altura ({style.height ?? 72}% da tela)</Rotulo>
          <input
            type="range" min={40} max={100} step={2}
            value={style.height ?? 72}
            onChange={e => patch({ height: Number(e.target.value) })}
            className="w-full accent-primary"
          />
        </label>
      )}
    </div>
  )
}

// ─── Barra de ferramentas de texto ─────────────────────────────────────────

/** O que está selecionado na página agora. */
interface Selecao {
  bloco: BlockId
  campo: string
  /** Chave dentro do item, quando o clique foi num campo de lista. */
  subcampo?: string
}

/**
 * O campo de texto por trás de uma seleção, ou null quando o que foi clicado
 * não é texto — imagem, botão, o bloco inteiro. É o que decide se a barra
 * acende: oferecer fonte e alinhamento para um upload de imagem seria ruído.
 */
function campoDeTexto(sel: Selecao): SimpleField | null {
  const def = BLOCK_BY_ID[sel.bloco]
  if (!def) return null
  const f = def.fields.find(x => x.key === sel.campo)
  if (!f) return null
  const alvo = isListField(f)
    ? (sel.subcampo ? f.fields.find(x => x.key === sel.subcampo) : undefined)
    : f
  if (!alvo) return null
  return alvo.type === 'text' || alvo.type === 'textarea' ? alvo : null
}

/** Select compacto da barra. A opção vazia sempre significa "herda o padrão". */
function Sel({ value, options, onChange, titulo }: {
  value: string
  options: { value: string; label: string }[]
  onChange: (v: string) => void
  titulo: string
}) {
  return (
    <select
      value={value} title={titulo} onChange={e => onChange(e.target.value)}
      className="h-7 rounded-md border border-border bg-background px-1.5 text-[11px] text-foreground outline-none focus:border-primary/60"
    >
      {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  )
}

/**
 * Barra de aparência do texto selecionado.
 *
 * Fica ancorada no topo da área de preview, e não flutuando sobre o elemento:
 * o balão flutuante já existiu aqui e foi removido porque a borda da área o
 * cortava. Ancorada, ela nunca é cortada e não disputa espaço com a página.
 */
function BarraTexto({ sel, config, aplicar, limpar, enviarFonte }: {
  sel: Selecao | null
  config: LandingConfig
  aplicar: (p: Partial<EstiloCampo>) => void
  limpar: () => void
  enviarFonte: (f: File) => Promise<void>
}) {
  const ref = useRef<HTMLInputElement>(null)
  const [enviando, setEnviando] = useState(false)
  const [erro, setErro] = useState<string | null>(null)

  const campo = sel ? campoDeTexto(sel) : null
  const def = sel ? BLOCK_BY_ID[sel.bloco] : null
  const est: EstiloCampo = sel && campo
    ? estiloCampo(sel.bloco, config.blocks[sel.bloco].style, sel.campo, sel.subcampo)
    : {}

  const subir = async (f: File) => {
    setEnviando(true); setErro(null)
    try { await enviarFonte(f) } catch (e) {
      setErro(e instanceof Error ? e.message : 'falha no envio')
    } finally { setEnviando(false) }
  }

  return (
    <div className="mb-2 flex flex-shrink-0 flex-wrap items-center gap-x-3 gap-y-1.5 rounded-md border border-border bg-background px-2.5 py-1.5">
      {!campo || !sel || !def ? (
        <span className="text-[11px] text-muted-foreground">
          Clique num texto da página para mudar alinhamento, tamanho, peso, cor e fonte.
        </span>
      ) : (
        <>
          <span className="max-w-[200px] truncate text-[11px] font-medium text-foreground">
            {def.label} · {campo.label}
          </span>
          <span className="h-4 w-px bg-border" />

          <div className="inline-flex overflow-hidden rounded-md border border-border">
            {(['left', 'center', 'right'] as AlignCampo[]).map(a => {
              const Icone = a === 'left' ? FiAlignLeft : a === 'center' ? FiAlignCenter : FiAlignRight
              return (
                <button
                  key={a} type="button" title={ALIGN_CAMPO_LABELS[a]}
                  // Clicar no que já está ativo desliga: volta ao padrão do
                  // campo em vez de prender o usuário na primeira escolha.
                  onClick={() => aplicar({ align: est.align === a ? undefined : a })}
                  className={`flex h-7 w-7 items-center justify-center transition-colors ${
                    est.align === a ? 'bg-primary/10 text-primary'
                                    : 'text-muted-foreground hover:bg-muted/50 hover:text-foreground'
                  }`}
                >
                  <Icone className="h-3.5 w-3.5" />
                </button>
              )
            })}
          </div>

          {/* Tamanho em número. Vazio = herda o do tema para aquele campo. */}
          <label className="inline-flex items-center gap-1" title="Tamanho da fonte em px (na tela grande; encolhe sozinho no celular)">
            <input
              type="number" min={TAM_MIN} max={TAM_MAX} step={1}
              value={est.tam ?? ''} placeholder="—"
              onChange={e => {
                const v = e.target.value
                if (!v) return aplicar({ tam: undefined })
                const n = Number(v)
                if (Number.isFinite(n)) aplicar({ tam: Math.min(TAM_MAX, Math.max(TAM_MIN, Math.round(n))) })
              }}
              className="h-7 w-14 rounded-md border border-border bg-background px-1.5 text-[11px] text-foreground outline-none focus:border-primary/60"
            />
            <span className="text-[10px] text-muted-foreground">px</span>
          </label>

          <Sel
            titulo="Peso" value={est.peso ?? ''}
            onChange={v => aplicar({ peso: (v || undefined) as PesoCampo | undefined })}
            options={[
              { value: '', label: 'Peso' },
              ...(Object.keys(PESO_CAMPO_LABELS) as PesoCampo[]).map(p => ({ value: p, label: PESO_CAMPO_LABELS[p] })),
            ]}
          />

          {/* Itálico, sublinhado e caixa alta — alternam, então clicar de novo
              desliga, igual ao alinhamento. */}
          <div className="inline-flex overflow-hidden rounded-md border border-border">
            {([
              { k: 'italico', titulo: 'Itálico', conteudo: <FiItalic className="h-3.5 w-3.5" /> },
              { k: 'sublinhado', titulo: 'Sublinhado', conteudo: <FiUnderline className="h-3.5 w-3.5" /> },
              { k: 'caixaAlta', titulo: 'CAIXA ALTA', conteudo: <span className="text-[10px] font-semibold leading-none">AA</span> },
            ] as const).map(b => (
              <button
                key={b.k} type="button" title={b.titulo}
                onClick={() => aplicar({ [b.k]: est[b.k] ? undefined : true })}
                className={`flex h-7 w-7 items-center justify-center transition-colors ${
                  est[b.k] ? 'bg-primary/10 text-primary'
                           : 'text-muted-foreground hover:bg-muted/50 hover:text-foreground'
                }`}
              >
                {b.conteudo}
              </button>
            ))}
          </div>

          <Sel
            titulo="Entrelinha" value={est.entrelinha ?? ''}
            onChange={v => aplicar({ entrelinha: (v || undefined) as EntrelinhaCampo | undefined })}
            options={[
              { value: '', label: 'Entrelinha' },
              ...(Object.keys(ENTRELINHA_LABELS) as EntrelinhaCampo[]).map(t => ({ value: t, label: ENTRELINHA_LABELS[t] })),
            ]}
          />

          <Sel
            titulo="Espaçamento entre letras" value={est.espacamento ?? ''}
            onChange={v => aplicar({ espacamento: (v || undefined) as EspacamentoCampo | undefined })}
            options={[
              { value: '', label: 'Letras' },
              ...(Object.keys(ESPACAMENTO_LABELS) as EspacamentoCampo[]).map(t => ({ value: t, label: ESPACAMENTO_LABELS[t] })),
            ]}
          />

          {/* Cor: as da marca à frente, livre no seletor ao lado. */}
          <div className="inline-flex items-center gap-1">
            {CORES_MARCA.map(c => (
              <button
                key={c.valor} type="button" title={c.label}
                onClick={() => aplicar({ cor: est.cor === c.valor ? undefined : c.valor })}
                className={`h-5 w-5 rounded border transition-transform ${
                  est.cor === c.valor ? 'scale-110 border-primary' : 'border-border'
                }`}
                style={{ background: c.valor }}
              />
            ))}
            <input
              type="color" title="Cor livre" value={est.cor ?? '#613400'}
              onChange={e => aplicar({ cor: e.target.value })}
              className="h-5 w-6 cursor-pointer rounded border border-border bg-transparent p-0"
            />
          </div>

          <Sel
            titulo="Fonte" value={est.fonte ?? ''}
            onChange={v => aplicar({ fonte: v || undefined })}
            options={[
              { value: '', label: 'Fonte do tema' },
              ...(config.fontes ?? []).map(f => ({ value: f.nome, label: f.nome })),
            ]}
          />

          <button
            type="button" onClick={() => ref.current?.click()} disabled={enviando}
            title="Enviar uma fonte (WOFF2, WOFF, TTF ou OTF)"
            className="inline-flex h-7 items-center gap-1 rounded-md border border-border px-2 text-[11px] text-muted-foreground transition-colors hover:bg-muted/50 hover:text-foreground disabled:opacity-50"
          >
            {enviando ? <FiLoader className="h-3 w-3 animate-spin" /> : <FiUpload className="h-3 w-3" />}
            Fonte
          </button>
          <input
            ref={ref} type="file" className="hidden" accept=".woff2,.woff,.ttf,.otf"
            onChange={e => { const f = e.target.files?.[0]; if (f) subir(f); e.target.value = '' }}
          />

          <button
            type="button" onClick={limpar} title="Voltar este texto ao padrão"
            className="inline-flex h-7 items-center gap-1 rounded-md px-1.5 text-[11px] text-muted-foreground transition-colors hover:text-foreground"
          >
            <FiX className="h-3 w-3" /> Limpar
          </button>

          {erro && <span className="text-[10px] text-red-400">{erro}</span>}
        </>
      )}
    </div>
  )
}

// ─── Painel ────────────────────────────────────────────────────────────────

export default function LandingEditorPanel() {
  const { authenticatedFetch } = useAuth()
  const iframeRef = useRef<HTMLIFrameElement>(null)

  const [config, setConfig] = useState<LandingConfig | null>(null)
  const [salvo, setSalvo] = useState<string>('')      // JSON da última versão salva
  const [carregando, setCarregando] = useState(true)
  const [salvando, setSalvando] = useState(false)
  const [erro, setErro] = useState<string | null>(null)
  const [aberto, setAberto] = useState<BlockId | null>(null)
  const [mobile, setMobile] = useState(false)
  /**
   * Campo que o preview pediu para focar. Em vez de um balão flutuante — que
   * era cortado pela borda da área e duplicava a interface — a coluna lateral
   * abre no bloco, rola até o campo e o destaca.
   */
  const [foco, setFoco] = useState<{ bloco: BlockId; campo?: string; indice?: number } | null>(null)
  const refFoco = useRef<HTMLDivElement>(null)
  /**
   * Seleção da barra de ferramentas. Separada do `foco` de propósito: o foco
   * apaga sozinho depois de dois segundos, porque serve para achar o campo na
   * coluna; a barra precisa continuar apontando para o texto enquanto o usuário
   * mexe nos controles dela.
   */
  const [sel, setSel] = useState<Selecao | null>(null)

  /**
   * Histórico para desfazer. Guarda instantâneos em JSON — a config tem poucos
   * KB, então o custo é irrelevante perto de errar uma edição e não ter volta.
   * `ignorar` evita que o próprio desfazer entre na pilha.
   */
  const historico = useRef<string[]>([])
  const ignorar = useRef(false)

  const desfazer = () => {
    const anterior = historico.current.pop()
    if (!anterior) return
    ignorar.current = true
    setConfig(JSON.parse(anterior))
    setFoco(null)
  }

  // Rola a coluna até o campo focado e apaga o destaque depois de um tempo:
  // ele serve para achar o campo, não para ficar aceso.
  useEffect(() => {
    if (!foco) return
    const t1 = setTimeout(() => refFoco.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 60)
    const t2 = setTimeout(() => setFoco(null), 2200)
    return () => { clearTimeout(t1); clearTimeout(t2) }
  }, [foco])
  const areaRef = useRef<HTMLDivElement>(null)
  /** Lateral retrátil: no preview cheio o editor vira quase WYSIWYG. */
  const [lateral, setLateral] = useState(true)

  // Carga inicial da config salva.
  useEffect(() => {
    let vivo = true
    ;(async () => {
      try {
        const r = await authenticatedFetch(API_ENDPOINTS.LANDING_CONFIG)
        const j = await r.json()
        const c = normalizeConfig(j?.config)
        if (!vivo) return
        setConfig(c)
        setSalvo(JSON.stringify(c))
      } catch {
        if (!vivo) return
        const c = emptyConfig()
        setConfig(c)
        setSalvo(JSON.stringify(c))
      } finally {
        if (vivo) setCarregando(false)
      }
    })()
    return () => { vivo = false }
  }, [authenticatedFetch])

  // Espelha a config em edição no iframe. O preview só existe enquanto o
  // usuário está aqui, então não há custo em mandar a cada tecla.
  const espelhar = useCallback((c: LandingConfig) => {
    iframeRef.current?.contentWindow?.postMessage(
      { type: PREVIEW_MESSAGE, config: c }, window.location.origin,
    )
  }, [])

  useEffect(() => { if (config) espelhar(config) }, [config, espelhar])

  // Empilha o estado anterior sempre que a config muda por edição.
  const ultimo = useRef<string>('')
  const carimbo = useRef(0)
  useEffect(() => {
    if (!config) return
    const atual = JSON.stringify(config)
    if (ultimo.current && ultimo.current !== atual && !ignorar.current) {
      // Uma rajada de mudanças vira UM passo de desfazer. Arrastar o seletor de
      // cor dispara uma mudança por movimento, e digitar, uma por tecla: sem
      // agrupar, um arraste só consome os 60 passos e o Desfazer deixa de
      // servir justamente quando mais se precisa dele.
      const agora = Date.now()
      if (agora - carimbo.current > 500) {
        historico.current.push(ultimo.current)
        if (historico.current.length > 60) historico.current.shift()
      }
      carimbo.current = agora
    }
    ignorar.current = false
    ultimo.current = atual
  }, [config])

  // O iframe avisa quando montou; sem isso o primeiro postMessage se perde.
  useEffect(() => {
    const onMsg = (e: MessageEvent) => {
      if (e.origin !== window.location.origin) return
      const d = e.data
      if (!d?.type) return

      if (d.type === PREVIEW_PRONTO && config) { espelhar(config); return }

      // Clicaram num elemento na página: abre o bloco na lateral e o balão
      // sobre o próprio elemento.
      if (d.type === PREVIEW_SELECIONAR) {
        setAberto(d.bloco as BlockId)
        setFoco({ bloco: d.bloco as BlockId, campo: d.campo || undefined, indice: d.indice })
        setSel(d.campo ? { bloco: d.bloco as BlockId, campo: d.campo, subcampo: d.subcampo } : null)
        setLateral(true)   // clicou na página com a coluna recolhida: reabre
        return
      }

      // Texto digitado direto na página.
      if (d.type === PREVIEW_EDITAR) {
        setConfig(c => {
          if (!c) return c
          const bloco = c.blocks[d.bloco as BlockId]
          if (!bloco) return c
          let data = bloco.data
          if (d.indice === undefined || !d.subcampo) {
            data = { ...data, [d.campo]: d.valor }
          } else {
            const lista = [...((data[d.campo] as ListItem[]) ?? [])]
            if (!lista[d.indice]) return c
            lista[d.indice] = { ...lista[d.indice], [d.subcampo]: d.valor }
            data = { ...data, [d.campo]: lista }
          }
          return { ...c, blocks: { ...c.blocks, [d.bloco]: { ...bloco, data } } }
        })
        return
      }

      // Blocos reordenados por arraste no preview.
      if (d.type === PREVIEW_REORDENAR) {
        setConfig(c => (c ? { ...c, order: d.order as BlockId[] } : c))
        return
      }
    }
    window.addEventListener('message', onMsg)
    return () => window.removeEventListener('message', onMsg)
  }, [config, espelhar])

  /** Manda o preview destacar e rolar até um campo (clique veio da lateral). */
  const focarNoPreview = useCallback((bloco: BlockId, campo?: string, indice?: number, subcampo?: string) => {
    iframeRef.current?.contentWindow?.postMessage(
      { type: PREVIEW_FOCAR, bloco, campo, indice, subcampo }, window.location.origin,
    )
  }, [])

  const sujo = config != null && JSON.stringify(config) !== salvo

  const salvar = async () => {
    if (!config) return
    setSalvando(true); setErro(null)
    try {
      const r = await authenticatedFetch(API_ENDPOINTS.LANDING_CONFIG, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ config }),
      })
      const j = await r.json()
      if (!r.ok || !j.success) throw new Error(j.error || 'falha ao salvar')
      setSalvo(JSON.stringify(config))
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'falha ao salvar')
    } finally {
      setSalvando(false)
    }
  }

  const descartar = () => { if (salvo) setConfig(JSON.parse(salvo)) }

  const moverBloco = (i: number, delta: number) => {
    setConfig(c => {
      if (!c) return c
      const j = i + delta
      if (j < 0 || j >= c.order.length) return c
      const order = [...c.order]
      ;[order[i], order[j]] = [order[j], order[i]]
      return { ...c, order }
    })
  }

  const alternarBloco = (id: BlockId) =>
    setConfig(c => (c ? { ...c, blocks: { ...c.blocks, [id]: { ...c.blocks[id], enabled: !c.blocks[id].enabled } } } : c))

  const alterarDado = (id: BlockId, key: string, valor: string | ListItem[]) =>
    setConfig(c => (c ? { ...c, blocks: { ...c.blocks, [id]: { ...c.blocks[id], data: { ...c.blocks[id].data, [key]: valor } } } } : c))

  /** Aparência do texto selecionado — guardada no estilo do bloco, por campo. */
  const aplicarEstilo = (p: Partial<EstiloCampo>) => {
    if (!sel) return
    setConfig(c => {
      if (!c) return c
      const bloco = c.blocks[sel.bloco]
      const chave = chaveEstilo(sel.campo, sel.subcampo)
      const novo: EstiloCampo = { ...bloco.style.estiloCampos?.[chave], ...p }
      // Desligar um controle apaga a chave em vez de gravar `undefined`: assim
      // o campo volta ao padrão do blueprint e o JSON salvo não junta lixo.
      for (const k of Object.keys(novo) as (keyof EstiloCampo)[]) {
        if (novo[k] === undefined) delete novo[k]
      }
      const style = { ...bloco.style, estiloCampos: { ...bloco.style.estiloCampos, [chave]: novo } }
      return { ...c, blocks: { ...c.blocks, [sel.bloco]: { ...bloco, style } } }
    })
  }

  const limparEstilo = () => {
    if (!sel) return
    setConfig(c => {
      if (!c) return c
      const bloco = c.blocks[sel.bloco]
      const estiloCampos = { ...bloco.style.estiloCampos }
      delete estiloCampos[chaveEstilo(sel.campo, sel.subcampo)]
      return {
        ...c,
        blocks: { ...c.blocks, [sel.bloco]: { ...bloco, style: { ...bloco.style, estiloCampos } } },
      }
    })
  }

  /**
   * Sobe uma fonte e já aplica no texto selecionado. Reaproveita a rota de
   * upload da landing — o campo continua se chamando `image` por ser o nome que
   * o multer espera lá; quem valida o tipo é o backend, pela extensão.
   */
  const enviarFonte = async (file: File) => {
    const fd = new FormData()
    fd.append('image', file)
    const r = await authenticatedFetch(API_ENDPOINTS.LANDING_UPLOAD, { method: 'POST', body: fd })
    const j = await r.json()
    if (!r.ok || !j.success) throw new Error(j.error || 'falha no upload')

    const nome = nomeFonteSeguro(file.name.replace(/\.[^.]+$/, '')) || 'Fonte enviada'
    setConfig(c => {
      if (!c) return c
      // Mesmo nome sobrescreve: reenviar uma fonte corrigida troca o arquivo em
      // vez de criar uma segunda entrada com o mesmo rótulo na lista.
      const fontes = [...(c.fontes ?? []).filter(f => f.nome !== nome), { nome, url: j.url as string }]
      return { ...c, fontes }
    })
    aplicarEstilo({ fonte: nome })
  }

  const setConfigFn = (fn: (c: LandingConfig) => LandingConfig) =>
    setConfig(c => (c ? fn(c) : c))

  if (carregando || !config) {
    return (
      <div className="flex h-full items-center justify-center text-xs text-muted-foreground">
        Carregando a landing…
      </div>
    )
  }

  const ligados = config.order.filter(id => config.blocks[id].enabled).length

  return (
    <div className="flex h-full flex-col overflow-hidden">
      {/* Barra de ações */}
      <div className="flex flex-shrink-0 flex-wrap items-center gap-2 border-b border-border px-4 py-2">
        <span className="text-[11px] text-muted-foreground">
          {ligados} de {config.order.length} blocos ligados
        </span>
        {sujo && <span className="text-[11px] text-amber-500">· alterações não salvas</span>}
        {erro && <span className="text-[11px] text-red-400">· {erro}</span>}

        <div className="ml-auto flex items-center gap-1.5">
          <Seg
            value={mobile ? 'mobile' : 'desktop'}
            options={[{ value: 'desktop', label: 'Desktop' }, { value: 'mobile', label: 'Mobile' }]}
            onChange={v => setMobile(v === 'mobile')}
          />
          <a
            href={LANDING_PATH} target="_blank" rel="noreferrer"
            className="inline-flex items-center gap-1.5 h-8 px-2.5 rounded-md border border-border text-[11px] text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors"
          >
            <FiExternalLink className="w-3 h-3" /> Abrir
          </a>
          <button
            type="button" onClick={desfazer} disabled={historico.current.length === 0}
            className="inline-flex items-center gap-1.5 h-8 px-2.5 rounded-md border border-border text-[11px] text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors disabled:opacity-40"
            title="Desfazer a última alteração"
          >
            <FiCornerUpLeft className="w-3 h-3" /> Desfazer
          </button>
          <button
            type="button" onClick={descartar} disabled={!sujo}
            className="inline-flex items-center gap-1.5 h-8 px-2.5 rounded-md border border-border text-[11px] text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors disabled:opacity-40"
          >
            <FiRotateCcw className="w-3 h-3" /> Descartar
          </button>
          <button
            type="button" onClick={salvar} disabled={!sujo || salvando}
            className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md text-[11px] font-medium bg-foreground text-background neu-relief hover:opacity-90 transition-opacity disabled:opacity-40"
          >
            {salvando ? <FiLoader className="w-3 h-3 animate-spin" /> : <FiSave className="w-3 h-3" />}
            Salvar
          </button>
        </div>
      </div>

      {/* Split: blocos | preview */}
      <div className="flex min-h-0 flex-1 flex-col lg:flex-row">
        {/* Blocos */}
        <div
          className={`relative flex min-h-0 flex-col overflow-y-auto border-border transition-[max-width,padding] duration-300 lg:border-r ${
            lateral ? 'flex-1 p-3 lg:max-w-[440px]' : 'max-w-0 flex-none overflow-hidden p-0'
          }`}
        >
          <div className="flex flex-col gap-2">
            {config.order.map((id, i) => {
              const def = BLOCK_BY_ID[id]
              const state = config.blocks[id]
              const open = aberto === id
              return (
                <div key={id} className={`rounded-lg border border-border ${state.enabled ? '' : 'opacity-55'}`}>
                  <div className="flex items-center gap-1 px-2 py-1.5">
                    <button
                      type="button"
                      onClick={() => {
                        const novo = open ? null : id
                        setAberto(novo)
                        // Abrir na coluna acende o bloco na página: os dois
                        // lados sempre mostram o mesmo contexto.
                        if (novo) focarNoPreview(novo)
                      }}
                      className="flex min-w-0 flex-1 items-center gap-2 text-left"
                    >
                      {open ? <FiChevronDown className="w-3.5 h-3.5 shrink-0 text-muted-foreground" />
                            : <FiChevronRight className="w-3.5 h-3.5 shrink-0 text-muted-foreground" />}
                      <span className="shrink-0 text-[10px] tabular-nums text-muted-foreground">{def.n}</span>
                      <span className="truncate text-xs font-medium text-foreground">{def.label}</span>
                      <span
                        className="ml-1 h-3 w-3 shrink-0 rounded-full border border-border"
                        style={{ background: COMBOS[state.style.combo].bg }}
                        title={COMBO_LABELS[state.style.combo]}
                      />
                    </button>
                    {/* Caminho inverso: da lateral para a página. */}
                    <button type="button" onClick={() => focarNoPreview(id)}
                      className="p-1 text-muted-foreground hover:text-foreground" title="Mostrar na página">
                      <FiCrosshair className="w-3.5 h-3.5" />
                    </button>
                    <button type="button" onClick={() => moverBloco(i, -1)} disabled={i === 0}
                      className="p-1 text-muted-foreground hover:text-foreground disabled:opacity-25" title="Subir">
                      <FiArrowUp className="w-3.5 h-3.5" />
                    </button>
                    <button type="button" onClick={() => moverBloco(i, 1)} disabled={i === config.order.length - 1}
                      className="p-1 text-muted-foreground hover:text-foreground disabled:opacity-25" title="Descer">
                      <FiArrowDown className="w-3.5 h-3.5" />
                    </button>
                    <button type="button" onClick={() => alternarBloco(id)}
                      className="p-1 text-muted-foreground hover:text-foreground"
                      title={state.enabled ? 'Ocultar bloco' : 'Mostrar bloco'}>
                      {state.enabled ? <FiEye className="w-3.5 h-3.5" /> : <FiEyeOff className="w-3.5 h-3.5" />}
                    </button>
                  </div>

                  <div className={`lp-acc ${open ? 'lp-open' : ''}`}>
                    <div>
                    <div className="flex flex-col gap-3.5 border-t border-border px-3 py-3.5">
                      <p className="text-[11px] leading-relaxed text-muted-foreground">{def.hint}</p>

                      <Grupo titulo="Conteúdo" />

                      {def.fields.map(f => {
                        // Campo que veio de um clique na página: recebe o ref
                        // para a rolagem e um anel de destaque temporário.
                        const alvo = foco?.bloco === id && foco.campo === f.key
                        return (
                          <div
                            key={f.key}
                            ref={alvo ? refFoco : undefined}
                            className={`rounded-md transition-shadow duration-500 ${
                              alvo ? 'ring-2 ring-primary ring-offset-2 ring-offset-background' : ''
                            }`}
                          >
                            {isListField(f) ? (
                              <CampoLista
                                field={f}
                                items={(state.data[f.key] as ListItem[]) || []}
                                onChange={v => alterarDado(id, f.key, v)}
                                abrirIndice={alvo ? foco?.indice : undefined}
                              />
                            ) : (
                              <CampoSimples
                                field={f}
                                value={(state.data[f.key] as string) || ''}
                                onChange={v => alterarDado(id, f.key, v)}
                              />
                            )}
                          </div>
                        )
                      })}

                      <Grupo titulo="Aparência e posição" />
                      <ControlesEstilo def={def} config={config} setConfig={setConfigFn} />
                    </div>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>

          <p className="mt-3 px-1 text-[10px] leading-relaxed text-muted-foreground">
            As cores saem do guia de marca: só as combinações aprovadas de fundo e texto
            estão disponíveis. Um bloco sem nenhum campo preenchido não aparece na
            página pública, mesmo ligado.
          </p>
        </div>

        {/* Preview */}
        <div ref={areaRef} className="relative flex min-h-0 flex-1 flex-col overflow-hidden bg-muted/30 p-3">
          {/* Filipeta: recolhe a coluna e deixa o preview ocupar tudo. */}
          <button
            type="button"
            onClick={() => setLateral(v => !v)}
            title={lateral ? 'Recolher a coluna' : 'Mostrar a coluna'}
            className="absolute left-0 top-1/2 z-20 flex h-16 w-4 -translate-y-1/2 items-center justify-center rounded-r-md border border-l-0 border-border bg-background text-muted-foreground shadow-sm transition-colors hover:text-foreground"
          >
            {lateral ? <FiChevronLeft className="h-3.5 w-3.5" /> : <FiChevronRight className="h-3.5 w-3.5" />}
          </button>

          <BarraTexto
            sel={sel} config={config}
            aplicar={aplicarEstilo} limpar={limparEstilo} enviarFonte={enviarFonte}
          />

          <div className="flex min-h-0 flex-1 items-start justify-center">
            <iframe
              ref={iframeRef}
              // `motor=v1` explícito: sem ele, no dia em que a chave do banco
              // for para v2, este preview passa a desenhar a ÁRVORE DO PUCK
              // enquanto a pessoa digita na config v1 — ela edita, o preview não
              // mexe, e não há mensagem nenhuma. E só acontece quando o fetch
              // público do iframe ganha a corrida do fetch autenticado do painel,
              // ou seja: às vezes, que é o pior tipo de defeito para suporte.
              src={`${LANDING_PATH}?preview=1&motor=v1`}
              title="Preview da landing"
              className="h-full w-full rounded-lg border border-border bg-white shadow-sm"
              style={mobile ? { maxWidth: 390 } : undefined}
            />
          </div>
        </div>
      </div>
    </div>
  )
}
